import React, { useState, useEffect, Suspense, useMemo } from "react";
import dayjs from "@config/dayjs";
import SchedulerAPI from "../Scheduler/SchedulerAPI";
import { ISerialzeEvent } from "./IGridSections";
import { IEvent, ISectionsProps, IWeek } from "../ISections";
import * as enums from "@utils/enums";
import InfoSections from "../InfoSections/InfoSections";
import { Block, Day } from "../../../models/ISchema";
import type { Holidays } from "@services/types/holidays/holidaysTypes";
import EventDetailsModal from "./event-details-modal";
import ScheduleCalendarSkeleton from "./Skeleton/ScheduleCalendar.skeleton";
import { getActiveWeeks } from "./gridSections.utils";
import UnassignedSessions from "./components/unassigned-sessions/UnassignedSessions";
import type { UnassignedSessionRow } from "./components/unassigned-sessions/types";
import styles from "./grid-sections.module.scss";

const SchedulerLazyComponent = React.lazy(() => import("../Scheduler/Scheduler"));

const DAY_BY_INDEX = [
  Day.Sunday,
  Day.Monday,
  Day.Tuesday,
  Day.Wednesday,
  Day.Thursday,
  Day.Friday,
  Day.Saturday,
] as const;

interface GridSectionsProps extends ISectionsProps {
  holidays: Holidays;
}

const GridSections: React.FC<GridSectionsProps> = props => {
  const SAPI = new SchedulerAPI();
  const [state, setState] = useState({
    loading: true,
    events: props.data.eventList,
    activeInfoModal: false,
    activeInfoModalAccordion: false,
    currentSelectedEvent: {} as any,
    errorSelectedEvent: false,
    currentWeekList: props.data.weekList,
    updatedWeekList: [],
    // The `new Date()` branch is a legacy "no week" placeholder: it only runs
    // with an empty `weekList`, and every read downstream (`.id`,
    // `.startingDate`) resolves to undefined. Kept as-is to preserve behaviour.
    currentSelectedWeek: props.data.selectedWeek
      ? props.data.selectedWeek
      : props.data.weekList[0]
        ? props.data.weekList[0]
        : (new Date() as unknown as IWeek),
    unassigned: props.data.unassigned,
    notRequireSchedule: false,
  });

  useEffect(() => {
    const objWeek = props.currentWeek ? { currentSelectedWeek: props.currentWeek } : {};
    setState(prevState => ({
      ...prevState,
      events: props.data.eventList,
      currentWeekList: props.data.weekList,
      unassigned: props.data.unassigned,
      ...objWeek,
    }));
  }, [props]);

  const formatNewTimes = (event: any, start: Date, end: Date) => {
    const day = DAY_BY_INDEX[dayjs(start).day()];
    const startBlock: Block = {
      id: event.resource.blockRange.start.id,
      startingTime: dayjs(start).format("HH:mm:ss"),
      day,
    };
    const endBlock: Block = {
      id: event.resource.blockRange.end.id,
      endingTime: dayjs(end).format("HH:mm:ss"),
      day,
    };
    return {
      start: startBlock,
      end: endBlock,
    };
  };

  const serializeErrorList = (eventStatus: any) => {
    const { data } = props;
    const tmpListError: Array<IEvent> = data.eventList || [];
    const commitedError = eventStatus.commited;
    tmpListError.forEach((item, index) => {
      if (item.sessionId === eventStatus.sessionId) {
        tmpListError[index]["error"] =
          commitedError === false
            ? { status: true, details: eventStatus.validationErrors }
            : { status: false, details: [] };
        tmpListError[index]["state"] = enums.EVENT_STATES.UPDATED;
      }
    });
    const newData = { ...data, eventList: tmpListError };
    return newData;
  };

  const eventHandlers = {
    onEventDrop: ({ event, start, end }: ISerialzeEvent) => {
      const times = formatNewTimes(event, start, end);
      const eventObj = {
        ...event,
        resource: {
          ...event.resource,
          blockRange: {
            start: times.start,
            end: times.end,
          },
        },
      };

      props?.onOpenEdit?.({
        sessionId: String(event?.sessionId),
        currentSelectedWeek: state.currentSelectedWeek,
        event: eventObj,
      });
    },
    onEventResize: ({ event, start, end }: ISerialzeEvent) => {
      const times = formatNewTimes(event, start, end);
      const eventObj = {
        ...event,
        resource: {
          ...event.resource,
          blockRange: {
            start: times.start,
            end: times.end,
          },
        },
      };

      props?.onOpenEdit?.({
        sessionId: String(event?.sessionId),
        currentSelectedWeek: state.currentSelectedWeek,
        event: eventObj,
      });
    },
    filterCategories: (
      e: React.FormEvent<HTMLSelectElement>,
      { id, checked, filtercriteria }: { id: number; checked: boolean; filtercriteria: string },
    ) => {
      console.log(e, id, checked, filtercriteria);
    },
    onFilterRestrictedAreas: (
      e: React.FormEvent<HTMLSelectElement>,
      { areatype, checked }: { areatype: string; checked: boolean },
    ) => {
      SAPI.filterRestrictedAreas({ areatype, checked });
    },
    onSelectEvent: (event: IEvent) => {
      let errorState = false;
      if (event.error.status) {
        errorState = true;
      }
      setState(prevState => ({
        ...prevState,
        currentSelectedEvent: event,
        activeInfoModal: true,
        errorSelectedEvent: errorState,
        notRequireSchedule: false,
      }));
    },
    onToolbarNavigate: (date: Date, view: string, action: string) => {
      const { currentSelectedWeek, currentWeekList, updatedWeekList } = state;
      const weekList = updatedWeekList.length ? updatedWeekList : currentWeekList;
      let newSelectedWeek = currentSelectedWeek;
      const list = getActiveWeeks(weekList);
      const elementToFind: IWeek = currentSelectedWeek ? currentSelectedWeek : ({} as IWeek);
      const currentIndex = list.findIndex(
        (element: IWeek) => element.startingDate === elementToFind.startingDate,
      );

      if (action === "PREV" && list.length) {
        newSelectedWeek = currentIndex > 0 ? list[currentIndex - 1] : list[list.length - 1];
      }

      if (action === "NEXT" && list.length) {
        newSelectedWeek = currentIndex < list.length - 1 ? list[currentIndex + 1] : list[0];
      }

      if (props.setCurrentWeek) {
        props.setCurrentWeek(newSelectedWeek);
      }
      setState(prevState => ({ ...prevState, currentSelectedWeek: newSelectedWeek }));
    },
    closeModals: () => {
      setState(prevState => ({ ...prevState, activeInfoModal: false }));
    },
  };

  const undoPosition = (event: IEvent) => {
    const { data, setDataGrid } = props;
    const currentEvent = { ...event };
    SAPI.moveEvent(data.eventList, currentEvent);
    setState(prevState => ({ ...prevState, events: data.eventList }));

    const eventStatus = {
      event: currentEvent,
      sessionId: currentEvent.sessionId,
      commited: true,
      validationErrors: [],
    };
    const newData = serializeErrorList(eventStatus);
    setDataGrid(newData, () => {
      setState(prevState => ({
        ...prevState,
        events: data.eventList,
        activeInfoModal: false,
      }));
    });
  };

  const onSelectEventUnassigned = ({ session, interval }: UnassignedSessionRow) => {
    setState(prevState => ({
      ...prevState,
      currentSelectedEvent: { ...session, id: session.sessionId, intervals: interval },
      activeInfoModal: true,
      errorSelectedEvent: false,
      notRequireSchedule: true,
    }));
  };

  const listUnassigned = () => (
    <UnassignedSessions
      sessions={state.unassigned}
      selectedWeekId={state.currentSelectedWeek?.id}
      onSelect={onSelectEventUnassigned}
    />
  );

  const handleUpdateWeeks = (weeks: IWeek[]) => {
    setState(prevState => ({ ...prevState, updatedWeekList: weeks }));
  };

  const {
    events,
    activeInfoModal,
    currentSelectedEvent,
    errorSelectedEvent,
    currentWeekList,
    currentSelectedWeek,
    unassigned,
  } = state;
  const { config, data, dimension } = props;

  const dateToDisplay = useMemo(() => {
    if (currentSelectedWeek && currentSelectedWeek.startingDate) {
      return currentSelectedWeek.startingDate;
    } else {
      const dateDisplay = data.eventList[0]
        ? data.eventList[0].start
        : data.weekList[0]
          ? data.weekList[0].startingDate
          : new Date();
      return dateDisplay;
    }
  }, [currentSelectedWeek, data.eventList, data.weekList]);

  return (
    <React.Fragment>
      <EventDetailsModal
        isOpen={activeInfoModal}
        event={currentSelectedEvent}
        selectedWeek={currentSelectedWeek}
        eventHasError={errorSelectedEvent}
        dimension={dimension}
        onClose={() => eventHandlers.closeModals()}
        onUndoPosition={(event: IEvent) => undoPosition(event)}
      />

      <div className={styles.gridInfo}>
        <div className={styles.infoWeeks}>
          <InfoSections
            defaultTerm={props.defaultTerm}
            onUpdateWeeks={handleUpdateWeeks}
            onSelectWeek={(currentSelectedWeek: IWeek) => {
              if (props.setCurrentWeek) {
                props.setCurrentWeek(currentSelectedWeek);
              }

              setState(prevState => ({ ...prevState, currentSelectedWeek }));
            }}
            weeks={currentWeekList}
            selected={currentSelectedWeek}
          />
        </div>
        <div className={styles.sectionGrid}>
          <Suspense fallback={<ScheduleCalendarSkeleton />}>
            <SchedulerLazyComponent
              holidays={props.holidays}
              config={config}
              eventHandlers={eventHandlers}
              events={events}
              restrictions={data.calendarRestrictions}
              dateToDisplay={dateToDisplay}
              dataUnassigned={unassigned}
              currentSelectedWeek={currentSelectedWeek}
              listUnassigned={listUnassigned}
            />
          </Suspense>
        </div>
      </div>
    </React.Fragment>
  );
};

export default GridSections;
